import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faFileCsv, faCheckCircle, faExclamationTriangle,
  faUser, faEdit, faTrash, faClock, faCalendarAlt,
  faSyncAlt, faCode, faInfoCircle, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie';
import { PresencesContext } from '../layout/CunstomPresences';

// ============================================================
// UTILITAIRE FETCH JSON SÉCURISÉ
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();

  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }

  try {
    return JSON.parse(text);
  } catch (parseError) {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
  }
};

// --- Cache Dexie (avec fallback périmé) ---
const db = new Dexie('HistoriquePresenceCacheDB');
db.version(1).stores({ history: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedHistory(key, allowExpired = false) {
  try {
    const entry = await db.history.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}
async function setCachedHistory(key, data) {
  await db.history.put({ key, data, timestamp: Date.now() });
}
async function getStaleHistory(key) {
  return getCachedHistory(key, true);
}

// ============================================================
// DÉDOUBLONNAGE DES REQUÊTES EN COURS (Map globale)
// ============================================================
const pendingFetches = new Map();

const API_URL = `${API_CONFIG2.apiUrl}presence/api_historique_presence.php`;

// --- Fond de particules réseau (inchangé) ---
const ParticleCanvas = () => {
  const canvasRef = useRef(null);
  const particles = useRef([]);
  const animFrame = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let width, height;
    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      initParticles();
    };
    const initParticles = () => {
      particles.current = Array.from({ length: 60 }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        radius: Math.random() * 2 + 1,
      }));
    };
    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      const pts = particles.current;
      for (let i = 0; i < pts.length; i++) {
        const p = pts[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(59, 130, 246, 0.25)';
        ctx.fill();
        for (let j = i + 1; j < pts.length; j++) {
          const p2 = pts[j];
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 140) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(59, 130, 246, ${0.12 * (1 - dist / 140)})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }
      animFrame.current = requestAnimationFrame(draw);
    };
    resize();
    window.addEventListener('resize', resize);
    animFrame.current = requestAnimationFrame(draw);
    return () => {
      window.removeEventListener('resize', resize);
      if (animFrame.current) cancelAnimationFrame(animFrame.current);
    };
  }, []);

  return (
    <canvas ref={canvasRef} style={{ position: 'fixed', top: 0, left: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.6 }} />
  );
};

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const HistoriquePresence = () => {
  const context = useContext(PresencesContext) || {};
  const { refreshKey, setSource } = context;

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 10
  });
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [dataSource, setDataSource] = useState('cache');

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);

  // ============================================================
  // GESTION DES TOASTS
  // ============================================================
  const showToast = useCallback((message, variant = 'success', duration = 5000) => {
    setToast({ show: true, message, variant });
    setTimeout(() => {
      if (isMounted.current) setToast(prev => ({ ...prev, show: false }));
    }, duration);
  }, []);

  // ============================================================
  // RÉCUPÉRATION DE L'HISTORIQUE (avec dédoublonnage + annulation)
  // ============================================================
  const fetchHistory = useCallback(async (page = 1, search = '', forceRefresh = false, retryCount = 0) => {
    // Annuler la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = `page_${page}_search_${search}`;
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    // Dédoublonnage : si une requête identique est en cours, on l'attend
    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setHistory(result.data);
          setPagination(result.pagination);
          setDataSource('cache (partagé)');
          if (setSource) setSource('cache (partagé)');
          setLoading(false);
          setRefreshing(false);
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    // Limiter les tentatives pour éviter les boucles infinies
    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        showToast('Erreur : trop de tentatives. Veuillez réessayer plus tard.', 'danger');
      }
      return;
    }

    // Définir l'état de chargement
    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    const fetchPromise = (async () => {
      try {
        // 1. Cache (sauf forceRefresh)
        if (!forceRefresh) {
          const cached = await getCachedHistory(cacheKey);
          if (cached && isMounted.current) {
            setHistory(cached.data);
            setPagination(cached.pagination);
            setDataSource('cache');
            if (setSource) setSource('cache');
            setLoading(false);
            setRefreshing(false);
            return cached;
          }
        }

        // 2. Requête réseau
        const url = `${API_URL}?action=fetch&page=${page}&limit=${pagination.limit}&search=${encodeURIComponent(search)}`;
        const response = await fetch(url, { signal: controller.signal });

        // Gestion 429
        if (response.status === 429) {
          const stale = await getStaleHistory(cacheKey);
          if (stale && isMounted.current) {
            setHistory(stale.data);
            setPagination(stale.pagination);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            showToast('429 – Utilisation du cache périmé', 'warning');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          // Sinon, backoff exponentiel
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchHistory(page, search, forceRefresh, retryCount + 1);
        }

        if (!response.ok) {
          const stale = await getStaleHistory(cacheKey);
          if (stale && isMounted.current) {
            setHistory(stale.data);
            setPagination(stale.pagination);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            showToast(`Erreur réseau – Utilisation du cache`, 'warning');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (data.status === 'success') {
          const result = { data: data.data, pagination: data.pagination };
          if (isMounted.current) {
            setHistory(data.data);
            setPagination(data.pagination);
            const source = forceRefresh ? 'live (forcé)' : 'live';
            setDataSource(source);
            if (setSource) setSource(source);
            await setCachedHistory(cacheKey, result);
            if (forceRefresh) {
              showToast('Données actualisées', 'success');
            }
            setLoading(false);
            setRefreshing(false);
          }
          return result;
        } else {
          throw new Error(data.message || 'Erreur inconnue');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        // Dernier recours : cache périmé
        const stale = await getStaleHistory(cacheKey);
        if (stale && isMounted.current) {
          setHistory(stale.data);
          setPagination(stale.pagination);
          setDataSource('cache (périmé)');
          if (setSource) setSource('cache (périmé)');
          showToast(`Erreur – Utilisation du cache : ${err.message}`, 'warning');
          setLoading(false);
          setRefreshing(false);
          return stale;
        }
        if (isMounted.current) {
          showToast(err.message || 'Erreur de chargement', 'danger');
          setLoading(false);
          setRefreshing(false);
        }
        throw err;
      }
    })();

    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [pagination.limit, showToast, setSource]);

  // ============================================================
  // RÉACTION AU REFRESH GLOBAL (via contexte)
  // ============================================================
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      fetchHistory(pagination.currentPage, searchTerm, true);
    }
  }, [refreshKey, fetchHistory, pagination.currentPage, searchTerm]);

  // ============================================================
  // RECHERCHE (debounce)
  // ============================================================
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => fetchHistory(1, val, false), 400);
  };

  // ============================================================
  // INITIALISATION ET NETTOYAGE
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    fetchHistory();
    return () => {
      isMounted.current = false;
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
      pendingFetches.clear();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================
  // EXPORT CSV
  // ============================================================
  const exportCSV = () => {
    if (!history.length) {
      showToast('Aucune donnée à exporter.', 'danger');
      return;
    }
    const headers = ['ID', 'Présence ID', 'Action', 'Employé', 'Date présence', 'Anciennes données', 'Nouvelles données', 'Utilisateur', 'Date action'];
    const rows = history.map(h => [
      h.id,
      h.presence_id,
      h.action,
      `${h.employe_prenom || ''} ${h.employe_nom || ''}`.trim() || 'Inconnu',
      h.presence_date || '',
      h.old_data ? JSON.stringify(h.old_data) : '',
      h.new_data ? JSON.stringify(h.new_data) : '',
      h.user_id || '',
      h.date_action
    ]);
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `historique_presence_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ============================================================
  // MODALE DE DÉTAILS
  // ============================================================
  const openDetailModal = (record) => {
    setSelectedRecord(record);
    setShowDetailModal(true);
  };

  const getActionBadge = (action) => {
    const lower = action?.toLowerCase() || '';
    if (lower === 'insert' || lower === 'create') return { label: 'Création', color: '#10b981' };
    if (lower === 'update' || lower === 'edit') return { label: 'Modification', color: '#f59e0b' };
    if (lower === 'delete') return { label: 'Suppression', color: '#ef4444' };
    return { label: action || 'Inconnu', color: '#94a3b8' };
  };

  // ============================================================
  // RENDU AVEC STYLES RESPONSIFS OPTIMISÉS
  // ============================================================
  return (
    <>
      <style>{`
        /* ============================================================
           DARK BLUE GLASSMORPHISM – HISTORIQUE PRESENCE (amélioré + responsive)
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-glow: rgba(59, 130, 246, 0.35);
          --bg-primary: #0b1428;
          --card-bg: rgba(20, 40, 80, 0.5);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 18px;
        }

        .historique-presence-page {
          position: relative;
          z-index: 1;
          background: var(--bg-primary);
          background-image: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          min-height: 100vh;
          padding: clamp(0.75rem, 2vw, 2rem);
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .fade-up {
          opacity: 0;
          animation: fadeInUp 0.5s ease forwards;
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: clamp(1rem, 1.5vw, 1.8rem);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
          margin-bottom: 24px;
          transition: all 0.25s ease;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.3);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.08);
        }

        .badge-source {
          font-size: clamp(0.6rem, 0.9vw, 0.75rem);
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
          font-weight: 500;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .monetra-btn {
          padding: 8px 16px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn:disabled {
          opacity: 0.5;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: white;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 6px 24px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.p-2 {
          padding: 6px 10px;
          min-width: 36px;
          justify-content: center;
        }

        .search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 4px 4px 4px 16px;
          transition: 0.3s;
          flex: 1;
          min-width: 180px;
          max-width: 100%;
        }
        .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 15px rgba(59, 130, 246, 0.1);
        }
        .search-wrapper input {
          background: transparent;
          border: none;
          color: var(--text-primary);
          outline: none;
          padding: 10px 0;
          flex: 1;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .search-wrapper input::placeholder {
          color: var(--text-muted);
        }

        .glass-table {
          width: 100%;
          border-collapse: collapse;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
        }
        .glass-table th {
          padding: 12px 10px;
          text-align: left;
          font-size: clamp(0.6rem, 0.8vw, 0.75rem);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--accent-blue);
          border-bottom: 1px solid rgba(255,255,255,0.06);
          font-weight: 600;
        }
        .glass-table td {
          padding: 10px 10px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
          color: var(--text-secondary);
          vertical-align: middle;
        }
        .glass-table tr:hover td {
          background: rgba(255,255,255,0.03);
        }
        .glass-table .text-accent {
          color: var(--accent-blue);
          font-weight: 600;
        }

        .action-badge {
          display: inline-block;
          padding: 4px 14px;
          border-radius: 20px;
          font-size: clamp(0.6rem, 0.8vw, 0.7rem);
          font-weight: 600;
          letter-spacing: 0.3px;
          text-transform: uppercase;
        }

        .json-preview {
          background: rgba(10, 15, 31, 0.6);
          border-radius: 14px;
          padding: 16px;
          border: 1px solid rgba(59, 130, 246, 0.08);
          margin-top: 10px;
          max-height: 400px;
          overflow-y: auto;
        }
        .json-preview pre {
          margin: 0;
          font-family: 'Fira Code', monospace;
          font-size: clamp(0.75rem, 1vw, 0.85rem);
          white-space: pre-wrap;
          word-break: break-all;
          color: var(--text-primary);
        }
        .json-preview::-webkit-scrollbar { width: 6px; }
        .json-preview::-webkit-scrollbar-thumb { background: rgba(59, 130, 246, 0.3); border-radius: 4px; }

        .monetra-modal .modal-content {
          background: rgba(10, 15, 31, 0.96);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.08);
          border-radius: var(--radius);
          color: var(--text-primary);
          box-shadow: 0 30px 80px rgba(0,0,0,0.8);
        }
        .monetra-modal .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
          background: linear-gradient(135deg, rgba(59,130,246,0.08), transparent);
          border-radius: var(--radius) var(--radius) 0 0;
          padding: clamp(1rem, 1.5vw, 1.5rem) clamp(1.2rem, 2vw, 2rem);
        }
        .monetra-modal .modal-footer {
          border-top: 1px solid rgba(59, 130, 246, 0.06);
          padding: clamp(0.8rem, 1.5vw, 1.2rem) clamp(1.2rem, 2vw, 2rem);
        }
        .monetra-modal .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }
        .monetra-modal .modal-body {
          padding: clamp(1.2rem, 2vw, 2rem);
        }

        .toast-container {
          position: fixed;
          top: clamp(12px, 2vh, 24px);
          right: clamp(12px, 2vw, 24px);
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-width: 440px;
        }
        .toast-item {
          background: rgba(10, 15, 31, 0.85);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: var(--radius);
          padding: 12px 18px;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 14px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.5);
          animation: slideIn 0.4s ease;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .toast-item.success { border-left: 4px solid #10b981; }
        .toast-item.warning { border-left: 4px solid #f59e0b; }
        .toast-item.danger { border-left: 4px solid #ef4444; }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px) scale(0.96); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 4px;
          margin-left: auto;
        }
        .btn-close-custom:hover { color: var(--text-primary); }

        .flex-between {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }
        .gap-2 { gap: 8px; }
        .mb-4 { margin-bottom: clamp(1rem, 2vw, 1.5rem); }
        .me-2 { margin-right: 0.5rem; }
        .p-2 { padding: 0.5rem; }
        .p-3 { padding: 1rem; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .text-center { text-align: center; }
        .text-secondary { color: var(--text-secondary); }
        .text-danger { color: #ef4444; }
        .text-muted { color: var(--text-muted); }
        .fw-bold { font-weight: 600; }
        .small { font-size: clamp(0.65rem, 0.8vw, 0.85rem); }
        .display-6 {
          font-size: clamp(1.6rem, 4vw, 2.2rem);
          font-weight: 700;
        }
        .m-0 { margin: 0; }
        .overflow-hidden { overflow: hidden; }
        .table-responsive { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .border-top { border-top: 1px solid rgba(255,255,255,0.04); }
        .text-accent { color: var(--accent-blue); }
        .row { display: flex; flex-wrap: wrap; margin: 0 -12px; }
        .row .col-md-6 { flex: 0 0 50%; max-width: 50%; padding: 0 12px; }

        @media (max-width: 768px) {
          .historique-presence-page { padding: 12px; }
          .glass-card { padding: 1rem; }

          .flex-between {
            flex-direction: column;
            align-items: stretch;
          }
          .flex-between > div:first-child {
            text-align: center;
          }

          .search-wrapper {
            max-width: 100%;
            min-width: unset;
          }

          .glass-table th,
          .glass-table td {
            padding: 6px 8px;
            font-size: 0.7rem;
          }
          .glass-table .text-accent {
            font-weight: 500;
          }

          .monetra-btn {
            font-size: 0.75rem;
            padding: 6px 12px;
            min-height: 38px;
            flex: 1 0 auto;
            justify-content: center;
          }
          .monetra-btn.p-2 {
            padding: 4px 8px;
            min-width: 32px;
          }

          .monetra-modal .modal-footer {
            flex-direction: column-reverse;
          }
          .monetra-modal .modal-footer button {
            width: 100%;
            justify-content: center;
          }
          .row .col-md-6 {
            flex: 0 0 100%;
            max-width: 100%;
          }
          .json-preview {
            max-height: 250px;
          }
        }
        @media (max-width: 480px) {
          .monetra-btn {
            font-size: 0.7rem;
            padding: 4px 10px;
            min-height: 34px;
          }
          .glass-table th,
          .glass-table td {
            font-size: 0.6rem;
            padding: 4px 6px;
          }
          .json-preview pre {
            font-size: 0.7rem;
          }
        }
      `}</style>

      <ParticleCanvas />

      <div className="historique-presence-page">
        {/* Header avec badge source et boutons d'actualisation */}
        <div className="flex-between mb-4 fade-up" style={{ animationDelay: '0ms' }}>
          <div>
            <h1 className="display-6 fw-bold mb-1" style={{ background: 'linear-gradient(135deg, #e0e7ff, #a5b4fc)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Historique des présences
            </h1>
            <p className="text-secondary m-0" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.95rem)' }}>
              Consultez toutes les modifications apportées aux présences.
            </p>
          </div>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <span className={`badge-source ${dataSource === 'live' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'cache (partagé)' ? '🔄 Partage' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button
              className="monetra-btn"
              onClick={() => fetchHistory(pagination.currentPage, searchTerm, false)}
              disabled={loading || refreshing}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} /> Actualiser
            </button>
            <button
              className="monetra-btn"
              onClick={() => fetchHistory(pagination.currentPage, searchTerm, true)}
              disabled={loading || refreshing}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} style={{ color: '#ef4444' }} /> Forcer
            </button>
            <button className="monetra-btn" onClick={exportCSV} disabled={history.length === 0 || loading || refreshing}>
              <FontAwesomeIcon icon={faFileCsv} /> Exporter CSV
            </button>
          </div>
        </div>

        {/* Recherche */}
        <div className="glass-card flex-between fade-up" style={{ animationDelay: '150ms' }}>
          <div className="search-wrapper">
            <FontAwesomeIcon icon={faSearch} className="text-secondary" />
            <input
              type="text"
              placeholder="Rechercher (action, employé, ID...)"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <div className="text-secondary small">
            <span>{pagination.totalRecords} enregistrements</span>
          </div>
        </div>

        {/* Tableau */}
        <div className="glass-card p-0 overflow-hidden fade-up" style={{ animationDelay: '300ms' }}>
          <div className="table-responsive">
            <table className="glass-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Présence ID</th>
                  <th>Action</th>
                  <th>Employé</th>
                  <th>Date présence</th>
                  <th>Utilisateur</th>
                  <th>Date action</th>
                  <th className="text-end">Détails</th>
                </tr>
              </thead>
              <tbody>
                {loading && !refreshing ? (
                  <tr><td colSpan="8" className="text-center py-5"><div className="spinner-border text-primary" role="status" /></td></tr>
                ) : history.length === 0 ? (
                  <tr><td colSpan="8" className="text-center py-5 text-secondary">Aucun historique trouvé.</td></tr>
                ) : (
                  history.map(item => {
                    const { label, color } = getActionBadge(item.action);
                    return (
                      <tr key={item.id}>
                        <td><span className="fw-bold text-accent">#{item.id}</span></td>
                        <td><span className="text-secondary">#{item.presence_id}</span></td>
                        <td>
                          <span className="action-badge" style={{ backgroundColor: `${color}22`, color: color }}>
                            {label}
                          </span>
                        </td>
                        <td>
                          <span className="text-secondary">
                            {item.employe_prenom && item.employe_nom ? `${item.employe_prenom} ${item.employe_nom}` : 'Inconnu'}
                          </span>
                        </td>
                        <td><span className="text-secondary">{item.presence_date || '-'}</span></td>
                        <td><span className="text-secondary">{item.user_id || 'Système'}</span></td>
                        <td><span className="text-secondary" style={{ fontSize: '0.85rem' }}>{new Date(item.date_action).toLocaleString()}</span></td>
                        <td className="text-end">
                          <button className="monetra-btn p-2" onClick={() => openDetailModal(item)} disabled={loading || refreshing}>
                            <FontAwesomeIcon icon={faCode} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-3 border-top flex-between">
            <span className="text-secondary small">Page {pagination.currentPage} sur {pagination.totalPages}</span>
            <div className="d-flex gap-2">
              <button
                className="monetra-btn p-2"
                disabled={pagination.currentPage <= 1 || loading || refreshing}
                onClick={() => fetchHistory(pagination.currentPage - 1, searchTerm, false)}
              >
                ‹
              </button>
              <button
                className="monetra-btn p-2"
                disabled={pagination.currentPage >= pagination.totalPages || loading || refreshing}
                onClick={() => fetchHistory(pagination.currentPage + 1, searchTerm, false)}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modale de détails JSON */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered className="monetra-modal" size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">
            <FontAwesomeIcon icon={faInfoCircle} className="text-primary me-2" />
            Détails de l'enregistrement #{selectedRecord?.id}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRecord && (
            <>
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <div className="text-secondary small">Action</div>
                  <div>
                    <span className="action-badge" style={{ backgroundColor: getActionBadge(selectedRecord.action).color + '22', color: getActionBadge(selectedRecord.action).color }}>
                      {getActionBadge(selectedRecord.action).label}
                    </span>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="text-secondary small">Présence ID</div>
                  <div>#{selectedRecord.presence_id}</div>
                </div>
                <div className="col-md-6">
                  <div className="text-secondary small">Employé</div>
                  <div>{selectedRecord.employe_prenom && selectedRecord.employe_nom ? `${selectedRecord.employe_prenom} ${selectedRecord.employe_nom}` : 'Inconnu'}</div>
                </div>
                <div className="col-md-6">
                  <div className="text-secondary small">Date de la présence</div>
                  <div>{selectedRecord.presence_date || '-'}</div>
                </div>
                <div className="col-md-6">
                  <div className="text-secondary small">Utilisateur</div>
                  <div>{selectedRecord.user_id || 'Système'}</div>
                </div>
                <div className="col-md-6">
                  <div className="text-secondary small">Date de l'action</div>
                  <div>{new Date(selectedRecord.date_action).toLocaleString()}</div>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <div className="json-preview">
                    <h6 className="text-secondary small"><FontAwesomeIcon icon={faTrash} className="me-1" /> Anciennes données</h6>
                    <pre>{selectedRecord.old_data ? JSON.stringify(selectedRecord.old_data, null, 2) : 'Aucune'}</pre>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="json-preview">
                    <h6 className="text-secondary small"><FontAwesomeIcon icon={faEdit} className="me-1" /> Nouvelles données</h6>
                    <pre>{selectedRecord.new_data ? JSON.stringify(selectedRecord.new_data, null, 2) : 'Aucune'}</pre>
                  </div>
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDetailModal(false)}>Fermer</Button>
        </Modal.Footer>
      </Modal>

      {/* Toast */}
      {toast.show && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
            />
            <span>{toast.message}</span>
            <button className="btn-close-custom" onClick={() => setToast({ ...toast, show: false })}>✕</button>
          </div>
        </div>
      )}
    </>
  );
};

export default HistoriquePresence;